import { useState, useCallback, useEffect } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTimes } from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { useNavigate, useParams } from "react-router-dom";
import RecipientCard from "../components/RecipientCard";
import DocumentUpload from "../components/DocumentUpload";
import AddMessage from "../components/AddMessage";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useCreateTemplateMutation } from "../services/useCreateTemplateMutation";
import { useUploadTemplateDocument } from "../services/useUploadTemplateDocument";
import { toast } from "react-toastify";
import LoadingScreen from "../components/LoadingScreen";
import Spinner from "../components/Spinner";
import { USA_STATE_LIST } from "../constant/constant";
import { useGetTemplateDetails } from "../services/useGetTemplateDetails";
import { useEditTemplateMutation } from "../services/useEditTemplateMutation";

const CreateTemplatesPage = () => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    watch,
    setValue,
    trigger,
  } = useForm({
    defaultValues: {
      template_type: "generic",
    },
  });

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: "recipients",
  });

  const [isDocumentAccordionOpen, setIsDocumentAccordionOpen] = useState(true);
  const [isRecipientsAccordionOpen, setIsRecipientsAccordionOpen] =
    useState(true);
  const { id } = useParams();
  const { data: templateDetails, isLoading } = useGetTemplateDetails(id);

  const [isMessageAccordionOpen, setIsMessageAccordionOpen] = useState(true);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isNewUpload, setIsNewUpload] = useState(false);

  const navigate = useNavigate();

  const {
    mutateAsync: uploadDocument,
    isLoading: isUploadLoading,
    error: uploadError,
  } = useUploadTemplateDocument();
  const {
    mutateAsync: createTemplate,
    isLoading: isCreateTemplateLoading,
    error: createTemplateError,
  } = useCreateTemplateMutation();
  const { mutateAsync: editTemplate, isLoading: isEditTemplateLoading } =
    useEditTemplateMutation();

  const [recipientError, setRecipientError] = useState("");

  const toggleDocumentAccordion = () => {
    setIsDocumentAccordionOpen(!isDocumentAccordionOpen);
  };

  const toggleRecipientsAccordion = () => {
    setIsRecipientsAccordionOpen(!isRecipientsAccordionOpen);
  };

  const toggleMessageAccordion = () => {
    setIsMessageAccordionOpen(!isMessageAccordionOpen);
  };

  useEffect(() => {
    if (!id) return;
    setValue("templateName", templateDetails?.name);
    setValue("templateDescription", templateDetails?.description);
    setValue("emailSubject", templateDetails?.email_subject);
    setValue("emailMessage", templateDetails?.email_message);
    setValue("recipients", templateDetails?.signers);
    setValue("state", templateDetails?.state);
    setValue("webhookUrl", templateDetails?.webhook_url);
    setValue("template_type", templateDetails?.template_type);

    if (templateDetails?.docs?.link) {
      fetch(templateDetails?.docs?.link)
        .then((res) => res.blob())
        .then((blob) => {
          const file = new File([blob], "document.pdf", {
            type: "application/pdf",
          });
          setUploadedFile(file);
          setIsNewUpload(false);
          const objectUrl = URL.createObjectURL(file);
          setPreviewUrl(objectUrl);
          setValue("document", file);
        })
        .catch((error) => console.error("Error fetching document:", error));
    }
  }, [templateDetails, setValue, id]);

  const onSubmit = useCallback(
    async (data, isNext = false) => {
      setRecipientError("");

      // At least one recipient is required: the field editor assigns every
      // placed field to a signer (the "Select User" picker), so a template with
      // no recipients cannot have its fields created.
      if (fields.length === 0) {
        setRecipientError("At least one recipient is required.");
        return;
      }

      try {
        const isValidDocument = await trigger("document");
        const isValidTemplateName = await trigger("templateName");

        if (!isValidDocument) {
          return;
        }

        if (!isValidTemplateName) {
          return;
        }

        const {
          templateName,
          templateDescription,
          recipients,
          emailSubject,
          emailMessage,
          webhookUrl,
          document,
        } = data;

        let uploadedDocumentResponse = null;
        const fileToUpload = document || uploadedFile;

        if (fileToUpload && isNewUpload) {
          try {
            uploadedDocumentResponse = await uploadDocument({
              file: fileToUpload,
            });
          } catch (uploadError) {
            console.error("Document upload failed:", uploadError);
            return;
          }
        }

        const createTemplatePayload = {
          name: templateName,
          email_subject: emailSubject,
          description: templateDescription || "",
          email_message: emailMessage,
          webhook_url: webhookUrl || "",
          state: Number(data?.state),
          template_type: data?.template_type,
          signers: recipients.map((recipient, index) => ({
            name: recipient.name,
            email: recipient.email,
            signing_order: index + 1,
            signer_role_name: recipient.signer_role_name,
            signer_id: recipient.id,
          })),
          link: id
            ? templateDetails?.docs?.link
            : uploadedDocumentResponse?.data?.url || null,
        };

        let createTemplateResponse = null;
        try {
          if (id) {
            createTemplateResponse = await editTemplate({
              ...createTemplatePayload,
              id: id,
              active: 1,
            });
          } else {
            createTemplateResponse = await createTemplate(
              createTemplatePayload
            );
          }
        } catch (createTemplateError) {
          console.error("Create template failed:", createTemplateError);
          return;
        }

        const AddFieldsInDocumentData = {
          signers: createTemplateResponse?.data?.signersData?.map(
            (recipient) => ({
              name: recipient.name,
              email: recipient.email,
              signing_order: recipient.signing_order,
              signer_role_name: recipient.signer_role_name,
              signer_roles: recipient.signer_roles,
              signer_id: recipient.id,
            })
          ),
          link: isNewUpload
            ? uploadedDocumentResponse?.data?.url
            : templateDetails?.docs?.link || null,
          templateId: createTemplateResponse?.data?.templateId,
        };

        if (createTemplateResponse?.success) {
          toast.success(createTemplateResponse?.message);
        } else if (!createTemplateResponse?.success && data) {
          createTemplateResponse?.data?.map((error) => toast.error(error.msg));
        } else {
          toast.error(createTemplateResponse?.message);
        }

        if (isNext && createTemplateResponse?.success) {
          if (id) {
            navigate(`/send-template/${id}`, {
              state: { data: AddFieldsInDocumentData },
            });
          } else {
            navigate("/send-template", {
              state: { data: AddFieldsInDocumentData },
            });
          }
        }
      } catch (error) {
        console.error("Error during form submission:", error);
      }
    },
    [
      fields,
      navigate,
      createTemplate,
      toast,
      uploadDocument,
      trigger,
      uploadedFile,
    ]
  );

  const handleClear = () => {
    reset();
    setUploadedFile(null);
    setPreviewUrl(null);
    setValue("document", null);
    setRecipientError("");
    setSubmitted(false);
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleDiscard = () => {
    handleClear();
    navigate("/");
  };

  const handleNext = () => {
    handleSubmit((data) => onSubmit(data, true))();
  };

  const onMoveRecipient = useCallback(
    (dragIndex, hoverIndex) => {
      move(dragIndex, hoverIndex);
    },
    [move]
  );

  if (isLoading && id) {
    return <LoadingScreen />;
  }

  return (
    <>
      {(isUploadLoading ||
        isCreateTemplateLoading ||
        isEditTemplateLoading) && <Spinner />}
      <DndProvider backend={HTML5Backend}>
        <div className="min-h-screen flex flex-col bg-gray-100">
          <div className="sticky top-0 bg-white shadow-md p-4 flex justify-between items-center z-10">
            <h2 className="text-lg font-semibold">
              {id ? "Edit " : "Create "}Template
            </h2>
            <div className="flex items-center">
              <button
                data-tooltip-id="closeTooltip"
                onClick={handleOpenModal}
                className="focus:outline-none rounded-full p-2 hover:bg-gray-200 transition-colors duration-200"
              >
                <FontAwesomeIcon icon={faTimes} className="text-gray-600" />
              </button>
              <Tooltip
                id="closeTooltip"
                content="Close"
                place="bottom"
                style={{ zIndex: 10000 }}
              />
            </div>
          </div>

          <div className="container mx-auto p-4 md:p-8 flex-grow">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="max-w-2xl mx-auto space-y-6">
                <div>
                  <label
                    htmlFor="templateName"
                    className="block text-gray-700 text-sm font-bold mb-2"
                  >
                    Template Name
                  </label>
                  <input
                    type="text"
                    id="templateName"
                    placeholder="Enter template name"
                    {...register("templateName", {
                      required: "Template name is required",
                      validate: (value) => {
                        if (value.trim() === "") {
                          return "Template name cannot be empty or just spaces";
                        }
                        return true;
                      },
                    })}
                    className="shadow appearance-none ring-1 border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-[#cfc9ff]"
                  />
                  {errors.templateName && (
                    <p className="text-red-500 text-xs italic">
                      {errors.templateName.message}
                    </p>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="templateDescription"
                    className="block text-gray-700 text-sm font-bold mb-2"
                  >
                    Template Description
                  </label>
                  <textarea
                    id="templateDescription"
                    placeholder="Description (Optional)"
                    {...register("templateDescription")}
                    className="shadow appearance-none border ring-1 rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-[#cfc9ff] h-24"
                  />
                </div>
              </div>

              <div className="rounded-lg shadow-md overflow-hidden max-w-2xl mx-auto">
                <button
                  type="button"
                  className="flex items-center justify-between w-full py-4 px-6 font-semibold text-left text-gray-800 bg-gray-200 hover:bg-gray-300 focus:outline-none"
                  onClick={toggleDocumentAccordion}
                >
                  <span>Add Document</span>
                  <span>{isDocumentAccordionOpen ? "-" : "+"}</span>
                </button>
                {isDocumentAccordionOpen && (
                  <div className="p-6">
                    <DocumentUpload
                      uploadedFile={uploadedFile}
                      setUploadedFile={(file) => {
                        setUploadedFile(file);
                        setIsNewUpload(true);
                      }}
                      previewUrl={previewUrl}
                      setPreviewUrl={setPreviewUrl}
                      register={register}
                      errors={errors}
                      setValue={setValue}
                    />
                  </div>
                )}
              </div>

              <div className="rounded-lg shadow-md overflow-hidden max-w-2xl mx-auto">
                <button
                  type="button"
                  className="flex items-center justify-between w-full py-4 px-6 font-semibold text-left text-gray-800 bg-gray-200 hover:bg-gray-300 focus:outline-none"
                  onClick={toggleRecipientsAccordion}
                >
                  <span>Add Recipients</span>
                  <span>{isRecipientsAccordionOpen ? "-" : "+"}</span>
                </button>
                {isRecipientsAccordionOpen && (
                  <div className="p-6">
                    {fields.map((field, index) => (
                      <RecipientCard
                        key={field.id}
                        recipient={field}
                        index={index}
                        remove={remove}
                        register={register}
                        errors={errors}
                        moveRecipient={onMoveRecipient}
                        isEdit={id && field.email !== ""}
                      />
                    ))}
                    <button
                      type="button"
                      onClick={() =>
                        append({
                          name: "",
                          role: "",
                          email: "",
                          action: "Needs to Sign",
                        })
                      }
                      className="bg-[#cfc9ff] text-black hover:bg-[#948ae3]  py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                    >
                      <FontAwesomeIcon icon={faPlus} className="mr-2" /> Add
                      Recipient
                    </button>
                    {recipientError && (
                      <p className="text-red-500 text-xs italic">
                        {recipientError}
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div className="rounded-lg shadow-md overflow-hidden max-w-2xl mx-auto">
                <div className="p-6">
                  <label
                    htmlFor="state"
                    className="block text-gray-700 text-sm font-bold mb-2"
                  >
                    State:
                  </label>
                  <select
                    className="w-full bg-white border ring-1 rounded px-4 py-2 text-left text-sm focus:outline-none transition-all hover:bg-gray-100"
                    {...register("state")}
                  >
                    <option value="">Select State</option>
                    {USA_STATE_LIST?.map((state, i) => (
                      <option value={i + 1}>{state}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="rounded-lg shadow-md overflow-hidden max-w-2xl mx-auto">
                <div className="p-6">
                  <label
                    htmlFor="template_type"
                    className="block text-gray-700 text-sm font-bold mb-2"
                  >
                    Template Type:
                  </label>
                  <select
                    disabled={!!id}
                    className="w-full bg-white border ring-1 rounded px-4 py-2 text-left text-sm focus:outline-none transition-all hover:bg-gray-100"
                    {...register("template_type")}
                  >
                    {[
                      { value: "generic", label: "Generic" },
                      { value: "provider", label: "Provider" },
                      {
                        value: "collab_physician",
                        label: "Collaborating Physician",
                      },
                    ].map(({ value, label }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="rounded-lg shadow-md overflow-hidden max-w-2xl mx-auto">
                <button
                  type="button"
                  className="flex items-center justify-between w-full py-4 px-6 font-semibold text-left text-gray-800 bg-gray-200 hover:bg-gray-300 focus:outline-none"
                  onClick={toggleMessageAccordion}
                >
                  <span>Add Message</span>
                  <span>{isMessageAccordionOpen ? "-" : "+"}</span>
                </button>
                {isMessageAccordionOpen && (
                  <div className="p-6">
                    <AddMessage
                      register={register}
                      errors={errors}
                      uploadedFile={uploadedFile}
                      watch={watch}
                    />
                  </div>
                )}
              </div>
            </form>
          </div>

          <div className="sticky bottom-0 bg-white shadow-md p-4 flex justify-end items-center z-10 space-x-2">
            {uploadError && (
              <p className="text-red-500 text-xs italic">
                Error uploading document. Please try again.
              </p>
            )}
            {createTemplateError && (
              <p className="text-red-500 text-xs italic">
                Error creating template. Please try again.
              </p>
            )}
            <button
              type="button"
              onClick={handleNext}
              disabled={
                isUploadLoading ||
                isCreateTemplateLoading ||
                isEditTemplateLoading
              }
              className="bg-[#cfc9ff] text-black hover:bg-[#948ae3]  py-2 px-4 rounded focus:outline-none focus:shadow-outline"
            >
              Next
            </button>
          </div>

          {isModalOpen && (
            <div className="fixed top-0 left-0 w-full h-full bg-gray-500 bg-opacity-75 flex justify-center items-center z-50">
              <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
                <div className="flex justify-end">
                  <button
                    onClick={handleCloseModal}
                    className="text-gray-500 hover:text-gray-700 focus:outline-none"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
                <h2 className="text-lg font-semibold mb-4">
                  Discard your changes?
                </h2>
                <p className="text-gray-700 mb-4">
                  Do you want to discard changes to this template?
                </p>
                <div className="mt-4 flex justify-end space-x-4">
                  <button
                    onClick={handleDiscard}
                    className="bg-[#cfc9ff] text-black hover:bg-[#948ae3] py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                  >
                    Discard Changes
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </DndProvider>
    </>
  );
};

export default CreateTemplatesPage;
